<template>
  <span>
    <v-autocomplete
      v-debounce:700="searchClient"
      v-model="client"
      :append-icon="withEdit ? (client ? 'mdi-pencil' : null) : null"
      @click:append="openClientEditing()"
      prepend-icon="mdi-account"
      @click:prepend="openCustomerPortal"
      :disabled="disabled"
      :return-object="true"
      :items="clients"
      :item-text="`name`"
      :item-value="`id`"
      :loading="clientsRequest.loading"
      :clearable="!!client"
      :label="$lang('reservations.fields.client')"
      :error="!!errors.length"
      :error-messages="errors"
      :dense="dense"
      :rules="isRequired ? required : []"
      no-filter
    >
      <template v-slot:selection="{ item }">
        <v-icon v-if="item.is_blacklist" color="black" :title="$lang('clients.fields.is_blacklist')">mdi-alert-decagram</v-icon>
        {{ item.name }}
      </template>
      <template v-slot:item="{ item }">
        <v-list dense>
          <v-list-item>
            <v-list-item-icon>
              <v-icon v-if="item.is_blacklist" color="black">mdi-alert-decagram</v-icon>
              <country-flag v-else-if="!!item.country" :country="item.country" size="normal"/>
              <v-icon v-else :color="item.type === 1 ? 'indigo' : 'deep-orange'">mdi-account</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>
                {{ item.name }}
              </v-list-item-title>
              <v-list-item-subtitle>
                <span v-if="!!item.phone">
                  {{ $phoneFormat(item.phone) }}
                </span>
              </v-list-item-subtitle>
              <v-list-item-subtitle>
                {{ item.email }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </template>
      <template v-slot:prepend-item>
        <v-list-item v-if="!!searchTerm && !clientsRequest.loading" @click="openClientCreation()">
          <v-btn small color="primary" block>
            {{ $lang('main.crud.create') }} "{{ searchTerm }}"
          </v-btn>
        </v-list-item>
      </template>
      <template v-slot:no-data>
        <span v-if="!!searchTerm"></span>
        <v-list-item v-if="!searchTerm">
          <v-list-item-content>
            <v-list-item-title>{{ $lang('clients.hints.search') }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </template>
    </v-autocomplete>
    <slot v-if="$can('VIEW_CLIENT_DETAILS')" name="info" :info="info">
      <v-list v-if="client" dense>
        <v-list-item class="ml-0 pl-0">
          <v-list-item-avatar>
              <country-flag v-if="!!info.country" :country="info.country" size="normal"/>
              <v-icon v-else color="indigo">mdi-map-marker</v-icon>
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-title>{{ info.details }}</v-list-item-title>
            <v-list-item-subtitle>{{ info.subDetails }}</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
        <v-divider v-if="!!info.phone"></v-divider>
        <v-list-item v-if="!!info.phone" class="ml-0 pl-0">
          <v-list-item-icon class="mr-3">
            <v-btn :href="`tel://${$phoneFormat(info.phone)}`" icon>
              <v-icon color="green">mdi-phone</v-icon>
            </v-btn>
          </v-list-item-icon>
          <v-list-item-content class="ml-0 pl-0">
            <v-list-item-title>
              <a :href="`https://wa.me/${$phoneFormat(info.phone)}`" target="_blank">
                {{ $phoneFormat(info.phone) }}
                <v-icon color="green" small>mdi-whatsapp</v-icon>
              </a>
            </v-list-item-title>
            <v-list-item-title>
              <a :href="`mailto:${info.email}`" target="_blank" class="text-overline">
                <span>{{ info.email }}</span>
              </a>
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </slot>

    <v-dialog v-model="isOpenClientEditing" fullscreen hide-overlay transition="dialog-bottom-transition">
      <client-editing-form
        v-if="isOpenClientEditing"
        :item-id="client.id"
        @cancel="closeClientEditing"
        @error="failedClientEditing"
        @success="successClientEditing"
      />
    </v-dialog>

    <v-dialog v-model="isOpenClientCreation" fullscreen hide-overlay transition="dialog-bottom-transition">
      <client-creation-form
        v-if="isOpenClientCreation"
        :company-id="companyId"
        :client-name="searchTerm"
        @cancel="closeClientCreation"
        @error="failedClientCreation"
        @success="successClientCreation"
      />
    </v-dialog>
  </span>
</template>

<script>
  import { mapState } from "vuex";

  import {
    GET_CLIENTS_COLLECTION,
  } from "@/store/clients/actions";

  import {
    GET_PROPERTIES_SINGLE
  } from "@/store/properties/actions";

  import ClientEditingForm from "@/pages/clients/EditingForm";
  import ClientCreationForm from "@/pages/clients/CreationForm";

  export default {
    name: "VClient",
    components: {
      ClientEditingForm,
      ClientCreationForm,
    },
    props: {
      item: {
        required: false,
      },
      propertyId: {
        type: Number,
        required: false,
      },
      disabled: {
        type: Boolean,
        required: false,
        default: false
      },
      withEdit: {
        type: Boolean,
        required: false,
        default: true
      },
      errors: {
        type: Array,
        required: false,
        default() {
          return [];
        }
      },
      dense: {
        type: Boolean,
        required: false,
        default: false
      },
      isRequired: {
        type: Boolean,
        default: false,
      }
    },
    data() {
      return {
        searchTerm: null,
        client: null,
        clients: [],
        isOpenClientEditing: false,
        isOpenClientCreation: false,
        clientSelects: [
          'id',
          'nano_id',
          'company_id',
          'type',
          'name',
          'phone',
          'email',
          'country',
          'city',
          'address',
          'state',
          'postcode',
          'vat_code',
          'tax_code',
          'locale',
          'is_blacklist',
          'sdi',
          'pec',
        ],
        companyId: null,
        required: [
          v => !!v || this.$lang('auth.validation.required'),
        ],
      }
    },
    beforeMount() {
      this.initClient();
      this.initCompanyId();
    },
    methods: {
      initClient() {
        if (this.item && this.item.id) {
          this.client = this.item;
          this.clients = [this.item];
        }
      },
      initCompanyId() {
        if (!this.propertyId) {
          return;
        }

        this.$store.dispatch(GET_PROPERTIES_SINGLE, {
          id: this.propertyId,
          params: {
            _data: {
              selects: [
                'id',
                'company_id'
              ]
            }
          }
        }).then((response) => {
          this.companyId = response.company_id;
        });
      },
      searchClient(search) {
        this.searchTerm = search;
        let filters = {
          limit: 30,
          sortBy: 'name',
          order: 'asc',
          search,
          _data: {
            selects: this.clientSelects
          }
        };

        if (this.propertyId) {
          filters.property_id = this.propertyId;
        }

        this.$store.dispatch(GET_CLIENTS_COLLECTION, filters);
      },
      openClientCreation() {
        this.isOpenClientCreation = true;
      },
      closeClientCreation() {
        this.isOpenClientCreation = false;
      },
      openClientEditing() {
        this.isOpenClientEditing = true;
      },
      closeClientEditing() {
        this.isOpenClientEditing = false;
      },
      successClientCreation(response) {
        this.client = response;
        this.clients.push(this.client);

        this.closeClientCreation();
        this.showNotification(this.$lang("main.crud.success_create"), "success");
      },
      failedClientCreation(response) {
        const message = response && response.message ? response.message : this.$lang("main.crud.something_went_wrong");
        this.showNotification(message, "error");
      },
      successClientEditing(response) {
        this.clients.map((client) => {
          if (response.id === client.id) {
            this.client = response;
            Object.assign(client, response);
          }
        });

        this.closeClientEditing();
        this.showNotification(this.$lang("main.crud.success_update"), "success");
      },
      failedClientEditing() {
        const message = response && response.message ? response.message : this.$lang("main.crud.something_went_wrong");
        this.showNotification(message, "error");
      },
      openCustomerPortal() {
        window.open(this.$customerPortalUrl(`portal/reservations?token=${this.client.nano_id}`), '_blank').focus();
      }
    },
    computed: {
      info() {
        if (!this.client) {
          return {
            details: '---',
            subDetails: '',
          }
        }

        let details = [];
        let subDetails = [];

        if (this.client.country) {
          details.push(this.client.country);
        }
        if (this.client.city) {
          details.push(this.client.city);
        }

        if (!details.length) {
          details = '---';
        } else {
          details = details.join(', ');
        }

        if (this.client.address) {
          subDetails.push(this.client.address);
        }

        subDetails = subDetails.join(', ');

        return {
          ...this.client,
          details,
          subDetails,
        }
      },
      ...mapState({
        clientsRequest: state => state.clients.getCollectionRequest,
      })
    },
    watch: {
      'item.id'() {
        this.initClient();
      },
      'client.id'() {
        if (this.client.is_blacklist) {
          this.showNotification(this.$lang('clients.fields.is_blacklist'), 'warning');
        }
        this.$emit('changed', this.client);
      },
      'clientsRequest.loading'(val) {
        if (!val) {
          this.clients = this.clientsRequest.collection;
        }
      }
    }
  }
</script>